import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AppShell } from "@/components/layout/AppShell";
import { useEffect, useState, useCallback, useRef } from "react";
import {
  Filter,
  Download,
  LayoutGrid,
  List,
  Calendar,
  Search,
  ListChecks,
  Plus,
  ArrowUpDown,
  SlidersHorizontal,
  ChevronDown,
  X,
  ArrowUp,
  ArrowDown,
  ArrowUpDown as SortIcon,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { asArray, asNumber } from "@/lib/safe";
import { licitacionesService, type ListParams } from "@/services/licitaciones.service";
import {
  estadoColors,
  estadoLabels,
  estadoMPColors,
  estadoMPLabels,
  formatCLP,
  diasRestantes,
} from "@/lib/mock-data";
import { etiquetaPlazo, evaluarElegibilidad } from "@/lib/licitacion-elegibilidad";
import { ORDEN_PRESETS, presetDeOrden } from "@/lib/orden-licitaciones";
import {
  CANALES,
  type CanalId,
  canalBadge,
  canalDeTipo,
  canalLabel,
  codigosDeCanal,
  estadoMPLabel,
  tipoMPLabel,
} from "@/lib/mercado-publico-catalog";

import type { LicitacionListItem, LicitacionEstado, TenantTracking } from "@/types";
import { cn } from "@/lib/utils";
import { FiltersSheet, type FiltersState } from "@/components/licitaciones/FiltersSheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LicitacionActionsMenu } from "@/components/licitaciones/LicitacionActionsMenu";
import { QuickViewDrawer } from "@/components/licitaciones/QuickViewDrawer";
import { Button } from "@/components/ui/button";

export type LicitacionesSearch = {
  estadoMP?: string;
  region?: string;
  categoria?: string;
  organismo?: string;
  fechaDesde?: string;
  fechaHasta?: string;
};

/** Filtros aceptados por URL — permiten enlazar desde el Dashboard. */
function validateSearch(search: Record<string, unknown>): LicitacionesSearch {
  const pick = (key: keyof LicitacionesSearch) =>
    typeof search[key] === "string" && search[key] ? (search[key] as string) : undefined;
  return {
    estadoMP: pick("estadoMP"),
    region: pick("region"),
    categoria: pick("categoria"),
    organismo: pick("organismo"),
    fechaDesde: pick("fechaDesde"),
    fechaHasta: pick("fechaHasta"),
  };
}

export const Route = createFileRoute("/licitaciones/")({
  head: () => ({ meta: [{ title: "Licitaciones — LicitaPro" }] }),
  validateSearch,
  component: Page,
});

type View = "tabla" | "kanban" | "calendario";

/** Carga ampliada para Tarjetas y Calendario: representan todo el universo filtrado. */
const BULK_MAX = 1000;

/** Columnas del tablero por estado oficial en Mercado Público. */
const boardCols: { id: string; label: string; color: string }[] = [
  { id: "abierta", label: "Publicada vigente", color: "bg-success/10 text-success" },
  { id: "por-vencer", label: "Por vencer", color: "bg-warning/10 text-warning" },
  { id: "plazo-vencido", label: "Plazo vencido", color: "bg-muted text-muted-foreground" },
  { id: "cerrada", label: "Cerrada", color: "bg-muted text-muted-foreground" },
  { id: "adjudicada", label: "Adjudicada", color: "bg-primary/10 text-primary" },
  { id: "desierta", label: "Desierta / revocada", color: "bg-destructive/10 text-destructive" },
];

/** Clasifica una licitación en una columna del tablero, sin depender del pipeline. */
function columnaDe(l: LicitacionListItem): string {
  const estado = (l.estadoMP ?? "").toLowerCase();
  if (estado === "adjudicada") return "adjudicada";
  if (estado === "desierta" || estado === "revocada" || estado === "suspendida") return "desierta";
  const elegibilidad = evaluarElegibilidad(l);
  if (elegibilidad.estado === "abierta") return "abierta";
  if (elegibilidad.estado === "por-vencer") return "por-vencer";
  if (elegibilidad.estado === "plazo-vencido") return "plazo-vencido";
  return "cerrada";
}

type SortableField =
  | "codigo"
  | "nombre"
  | "organismo"
  | "estadoMP"
  | "montoEstimado"
  | "fechaCierre"
  | "scoreIA"
  | "prioridad";

const sortableColumns: { key: SortableField; label: string }[] = [
  { key: "codigo", label: "Código" },
  { key: "nombre", label: "Licitación" },
  { key: "organismo", label: "Organismo" },
  { key: "estadoMP", label: "Estado" },
  { key: "montoEstimado", label: "Monto" },
  { key: "fechaCierre", label: "Cierre" },
  { key: "scoreIA", label: "Score IA" },
  { key: "prioridad", label: "Prioridad" },
];

/** Etiqueta legible para el filtro tipoMP (código único o lista de un canal). */
function etiquetaTipoFiltro(tipoMP: string): string {
  const codigos = tipoMP
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);
  if (codigos.length === 1) return tipoMPLabel(codigos[0]);
  const canal = CANALES.find(
    (c) =>
      codigosDeCanal(c.id).length === codigos.length &&
      codigosDeCanal(c.id).every((code) => codigos.includes(code)),
  );
  return canal ? canalLabel(canal.id) : codigos.join(", ");
}

/** Describe qué filtros activos hay para mostrar chips. */
function useActiveFilters(params: Partial<ListParams>): { key: string; label: string }[] {
  const chips: { key: string; label: string }[] = [];
  if (params.estado)
    chips.push({
      key: "estado",
      label: `Pipeline: ${estadoLabels[params.estado as LicitacionEstado] ?? params.estado}`,
    });
  if (params.prioridad) chips.push({ key: "prioridad", label: `Prioridad: ${params.prioridad}` });
  if (params.responsable)
    chips.push({ key: "responsable", label: `Responsable: ${params.responsable}` });
  if (params.region) chips.push({ key: "region", label: `Región: ${params.region}` });
  if (params.organismo) chips.push({ key: "organismo", label: `Organismo: ${params.organismo}` });
  if (params.categoria) chips.push({ key: "categoria", label: `Categoría: ${params.categoria}` });
  if (params.tipoMP)
    chips.push({ key: "tipoMP", label: `Tipo: ${etiquetaTipoFiltro(params.tipoMP)}` });
  if (params.estadoMP)
    chips.push({
      key: "estadoMP",
      // "todos" no es un estado de Mercado Público: es la salida explícita del
      // filtro por defecto, así que se nombra por lo que hace.
      label:
        params.estadoMP === "todos"
          ? "Incluye cerradas y desiertas"
          : `Estado MP: ${estadoMPLabel(params.estadoMP)}`,
    });
  if (params.fechaDesde) chips.push({ key: "fechaDesde", label: `Desde: ${params.fechaDesde}` });
  if (params.fechaHasta) chips.push({ key: "fechaHasta", label: `Hasta: ${params.fechaHasta}` });
  if (params.montoMin != null)
    chips.push({ key: "montoMin", label: `Monto ≥ $${(params.montoMin / 1_000_000).toFixed(1)}M` });
  if (params.montoMax != null)
    chips.push({ key: "montoMax", label: `Monto ≤ $${(params.montoMax / 1_000_000).toFixed(1)}M` });
  if (params.minScore != null && params.minScore > 0)
    chips.push({ key: "minScore", label: `Score ≥ ${params.minScore}` });
  return chips;
}

/** Filtros iniciales tomados de la URL (enlaces desde el Dashboard). */
function initialParamsFromUrl(): Partial<ListParams> {
  if (typeof window === "undefined") return {};
  const search = new URLSearchParams(window.location.search);
  const keys: (keyof LicitacionesSearch)[] = [
    "estadoMP",
    "region",
    "categoria",
    "organismo",
    "fechaDesde",
    "fechaHasta",
  ];
  const params: Partial<ListParams> = {};
  keys.forEach((key) => {
    const value = search.get(key);
    if (value) (params as Record<string, string>)[key] = value;
  });
  return params;
}

export function Page() {
  const { session, loading: authLoading } = useAuth();
  const [view, setView] = useState<View>("tabla");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [params, setParams] = useState<Partial<ListParams>>(initialParamsFromUrl);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [data, setData] = useState<LicitacionListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);

  // Sort state
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | undefined>(undefined);

  const activeChips = useActiveFilters(params);

  const hasSession = Boolean(session);

  const ordenActual = presetDeOrden(sortBy, sortOrder);

  // Vistas Tarjetas y Calendario: una sola petición al endpoint de carga masiva.
  const [truncado, setTruncado] = useState(false);
  // Caché por combinación filtros+orden para que alternar de vista no repita la consulta.
  const boardCache = useRef<{
    key: string;
    data: LicitacionListItem[];
    total: number;
    truncado: boolean;
  } | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      if (view === "tabla") {
        const res = await licitacionesService.list({
          ...params,
          sortBy,
          sortOrder,
          page,
          pageSize,
        });
        setData(asArray<LicitacionListItem>(res?.data));
        setTotal(asNumber(res?.total));
        setTruncado(false);
        return;
      }

      const key = JSON.stringify({ params, sortBy, sortOrder });
      const cache = boardCache.current;
      if (cache?.key === key) {
        setData(cache.data);
        setTotal(cache.total);
        setTruncado(cache.truncado);
        return;
      }

      const res = await licitacionesService.board({
        ...params,
        sortBy,
        sortOrder,
        pageSize: BULK_MAX,
      });
      const filas = asArray<LicitacionListItem>(res?.data);
      const totalRemoto = asNumber(res?.total);
      const cortado = Boolean(res?.truncado);
      boardCache.current = { key, data: filas, total: totalRemoto, truncado: cortado };
      setData(filas);
      setTotal(totalRemoto);
      setTruncado(cortado);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [params, sortBy, sortOrder, page, pageSize, view]);

  useEffect(() => {
    // Sin sesión activa las peticiones se abortan: esperamos al token.
    if (!hasSession) {
      if (!authLoading) setLoading(false);
      return;
    }
    fetchData();
  }, [fetchData, hasSession, authLoading]);

  // Frescura de los datos maestros: se sincronizan al menos una vez al día
  // de forma global y son iguales para todos los tenants.
  const [freshness, setFreshness] = useState<string | null>(null);
  useEffect(() => {
    if (!hasSession) return;
    licitacionesService
      .freshness()
      .then((f) => setFreshness(f.ultimaActualizacion))
      .catch(() => setFreshness(null));
  }, [hasSession]);

  const freshnessLabel = freshness
    ? `Datos al ${new Date(freshness).toLocaleString("es-CL", {
        timeZone: "America/Santiago",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })} hrs`
    : null;

  /** Aplicar filtros desde el sheet — resetea página y mergea con params existentes. */
  const handleApplyFilters = (newParams: Partial<ListParams>) => {
    setParams((prev) => ({ ...prev, ...newParams }));
    setPage(1);
    setSortBy(undefined);
    setSortOrder(undefined);
  };

  /** Remover un chip de filtro. */
  const removeChip = (key: string) => {
    const next = { ...params };
    delete (next as Record<string, unknown>)[key];
    setParams(next);
    setPage(1);
  };

  /** Ordenar por columna. */
  const handleSort = (field: string) => {
    if (sortBy === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else if (sortOrder === "desc") {
        setSortBy(undefined);
        setSortOrder(undefined);
      }
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return <SortIcon className="size-3 opacity-30" />;
    return sortOrder === "asc" ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />;
  };

  const handleExport = async () => {
    try {
      await licitacionesService.export("xlsx", { ...params, sortBy, sortOrder });
    } catch {
      // silent — error UI podría agregarse después
    }
  };

  return (
    <AppShell
      title="Licitaciones"
      subtitle={`${total} resultados · Fuente: Mercado Público + Convenio Marco${
        freshnessLabel ? ` · ${freshnessLabel}` : ""
      }`}
      actions={
        <button className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-gradient-primary text-primary-foreground shadow-glow hover:opacity-90 transition">
          <Plus className="size-3.5" /> Nueva
        </button>
      }
    >
      {/* FiltersSheet */}
      <FiltersSheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        current={params}
        onApply={handleApplyFilters}
      />
      <QuickViewDrawer
        id={previewId}
        open={Boolean(previewId)}
        onOpenChange={(open) => {
          if (!open) setPreviewId(null);
        }}
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-2 rounded-md border border-border bg-surface px-2.5 py-1.5 flex-1 min-w-[200px] max-w-md">
          <Search className="size-3.5 text-muted-foreground" />
          <input
            value={params.q ?? ""}
            onChange={(e) => {
              setParams((p) => ({ ...p, q: e.target.value || undefined }));
              setPage(1);
            }}
            placeholder="Buscar por código, nombre, organismo..."
            className="bg-transparent text-sm outline-none flex-1 placeholder:text-muted-foreground"
          />
        </div>
        <button
          onClick={() => setFiltersOpen(true)}
          className={cn(
            "inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border transition",
            activeChips.length > 0
              ? "border-primary/40 bg-primary/5 text-primary"
              : "border-border hover:bg-muted text-muted-foreground hover:text-foreground",
          )}
        >
          <SlidersHorizontal className="size-3.5" />
          Filtros avanzados
          {activeChips.length > 0 && (
            <span className="ml-1 text-[10px] bg-primary text-primary-foreground rounded-full size-4 grid place-items-center font-mono">
              {activeChips.length}
            </span>
          )}
        </button>

        {/* Export dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition">
              <Download className="size-3.5" /> Exportar <ChevronDown className="size-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[160px]">
            <DropdownMenuItem onClick={handleExport} className="text-sm cursor-pointer">
              <Download className="size-3.5 mr-2" /> Excel (.xlsx)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Orden */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition">
              <ArrowUpDown className="size-3.5" /> {ordenActual.label}{" "}
              <ChevronDown className="size-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[180px]">
            {ORDEN_PRESETS.map((preset) => (
              <DropdownMenuItem
                key={preset.id}
                onClick={() => {
                  setSortBy(preset.sortBy);
                  setSortOrder(preset.sortOrder);
                  setPage(1);
                }}
                className="text-sm cursor-pointer"
              >
                {preset.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="flex items-center rounded-md border border-border bg-surface p-0.5 ml-auto">
          {[
            { v: "tabla", I: List },
            { v: "kanban", I: LayoutGrid },
            { v: "calendario", I: Calendar },
          ].map(({ v, I }) => (
            <button
              key={v}
              onClick={() => setView(v as View)}
              className={cn(
                "size-7 grid place-items-center rounded transition",
                view === v
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <I className="size-3.5" />
            </button>
          ))}
        </div>
      </div>

      {/* El listado arranca mostrando sólo lo postulable. Sin este aviso, quien
          busca una licitación cerrada no encuentra nada y no sabe por qué:
          parece que la app no la tiene, cuando en realidad está filtrada. */}
      {!params.estadoMP && !params.fechaDesde && !params.fechaHasta && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-xs text-muted-foreground">
          <ListChecks className="size-3.5 text-primary" />
          <span>
            Mostrando sólo licitaciones{" "}
            <span className="text-foreground">abiertas a postulación</span>, las que cierran antes
            primero.
          </span>
          <button
            onClick={() => {
              setParams((p) => ({ ...p, estadoMP: "todos" }));
              setPage(1);
            }}
            className="ml-auto text-primary hover:underline"
          >
            Ver también cerradas y desiertas
          </button>
        </div>
      )}

      {/* Active filter chips */}
      {activeChips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {activeChips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20 text-primary"
            >
              {chip.label}
              <button onClick={() => removeChip(chip.key)} className="hover:text-primary/70">
                <X className="size-3" />
              </button>
            </span>
          ))}
          <button
            onClick={() => {
              setParams({});
              setPage(1);
            }}
            className="text-[11px] text-muted-foreground hover:text-foreground underline px-1"
          >
            Limpiar todo
          </button>
        </div>
      )}

      {loading ? (
        view === "tabla" ? (
          <div className="text-sm text-muted-foreground py-12 text-center">
            Cargando licitaciones…
          </div>
        ) : view === "kanban" ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
            {Array.from({ length: 5 }).map((_, col) => (
              <div key={col} className="space-y-2">
                <div className="h-6 animate-pulse rounded bg-muted" />
                {Array.from({ length: 4 }).map((__, card) => (
                  <div key={card} className="h-24 animate-pulse rounded-md bg-muted/60" />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: 35 }).map((_, i) => (
              <div key={i} className="h-24 animate-pulse rounded bg-muted/60" />
            ))}
          </div>
        )
      ) : error ? (
        <div className="grid place-items-center gap-3 rounded-md border border-border py-12 text-center">
          <div>
            <p className="font-medium text-foreground">No se pudo cargar el listado</p>
            <p className="text-sm text-muted-foreground">
              Revisa la conexión e inténtalo nuevamente.
            </p>
          </div>
          <button
            onClick={fetchData}
            className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"
          >
            Reintentar
          </button>
        </div>
      ) : (
        <>
          {view === "tabla" && (
            <TablaView
              data={data}
              total={total}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              sortBy={sortBy}
              onSort={handleSort}
              getSortIcon={getSortIcon}
              onPreview={setPreviewId}
              onTrackingChange={(id, tracking) =>
                setData((items) =>
                  items.map((item) => (item.id === id ? { ...item, tracking } : item)),
                )
              }
              onCanalFilter={(canal) =>
                handleApplyFilters({ tipoMP: codigosDeCanal(canal).join(",") })
              }
            />
          )}
          {view !== "tabla" && (
            <p className="mb-3 text-[11px] text-muted-foreground">
              Mostrando {data.length} de {total} licitaciones
              {truncado
                ? " · se alcanzó el máximo de carga, afina los filtros para ver el resto"
                : ""}
            </p>
          )}
          {view === "kanban" && <KanbanView data={data} />}
          {view === "calendario" && <CalendarView data={data} />}
        </>
      )}
    </AppShell>
  );
}

export function TablaView({
  data,
  total,
  page,
  pageSize,
  onPageChange,
  sortBy,
  onSort,
  getSortIcon,
  onPreview,
  onTrackingChange,
  onCanalFilter,
}: {
  data: LicitacionListItem[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  sortBy?: string;
  onSort: (field: string) => void;
  getSortIcon: (field: string) => React.ReactNode;
  onPreview: (id: string) => void;
  onTrackingChange: (id: string, tracking: TenantTracking) => void;
  /** Al hacer clic en el badge de canal, filtra la lista por ese canal. */
  onCanalFilter?: (canal: CanalId) => void;
}) {
  const navigate = useNavigate();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  const SortableTh = ({
    field,
    children,
    className,
  }: {
    field: string;
    children: React.ReactNode;
    className?: string;
  }) => (
    <th
      className={cn(
        "cursor-pointer select-none px-3 py-2.5 text-left font-medium transition hover:text-foreground",
        sortBy === field ? "text-foreground" : "text-muted-foreground",
        className,
      )}
      onClick={() => onSort(field)}
    >
      <div className="inline-flex max-w-full items-center gap-1 whitespace-nowrap">
        {children}
        {getSortIcon(field)}
      </div>
    </th>
  );

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-elegant">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1310px] table-fixed text-sm">
          <colgroup>
            <col className="w-[42px]" />
            <col className="w-[104px]" />
            <col className="w-[250px]" />
            <col className="w-[160px]" />
            <col className="w-[120px]" />
            <col className="w-[110px]" />
            <col className="w-[118px]" />
            <col className="w-[126px]" />
            <col className="w-[92px]" />
            <col className="w-[68px]" />
            <col className="w-[112px]" />
            <col className="w-[48px]" />
          </colgroup>

          <thead>
            <tr className="border-b border-border bg-surface-2/40 text-xs text-muted-foreground">
              <th className="px-3 py-2.5 text-center font-medium">
                <input
                  type="checkbox"
                  aria-label="Seleccionar todas"
                  className="rounded border-border"
                />
              </th>
              <SortableTh field="codigo">Código</SortableTh>
              <SortableTh field="nombre">Licitación</SortableTh>
              <SortableTh field="organismo">Organismo</SortableTh>
              <th className="px-3 py-2.5 text-left font-medium">Canal</th>
              <SortableTh field="estadoMP">Estado MP</SortableTh>
              <th className="px-3 py-2.5 text-left font-medium">Pipeline</th>

              <SortableTh field="montoEstimado" className="text-right tabular-nums">
                Monto
              </SortableTh>
              <SortableTh field="scoreIA" className="text-center">
                Score IA
              </SortableTh>
              <SortableTh field="fechaCierre" className="text-center">
                Cierre
              </SortableTh>
              <SortableTh field="prioridad">Responsable</SortableTh>
              <th className="px-2 py-2.5">
                <span className="sr-only">Acciones</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((l) => {
              const dias = l.fechaCierre ? diasRestantes(l.fechaCierre) : 0;
              const score = l.tracking?.scoreIA ?? 0;
              const responsable = l.tracking?.responsable ?? "—";
              return (
                <tr
                  key={l.id}
                  role="link"
                  tabIndex={0}
                  aria-label={`Abrir detalle de ${l.codigo}: ${l.nombre}`}
                  onClick={(event) => {
                    const target = event.target as HTMLElement;
                    if (target.closest("a, button, input, [role='menuitem']")) return;
                    void navigate({ to: "/licitaciones/$id", params: { id: l.id } });
                  }}
                  onKeyDown={(event) => {
                    const target = event.target as HTMLElement;
                    if (target.closest("a, button, input, [role='menuitem']")) return;
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    void navigate({ to: "/licitaciones/$id", params: { id: l.id } });
                  }}
                  className="cursor-pointer border-b border-border/60 transition last:border-0 hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                >
                  <td className="px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      aria-label={`Seleccionar ${l.codigo}`}
                      className="rounded border-border"
                    />
                  </td>
                  <td
                    className="whitespace-nowrap px-3 py-3 font-mono text-xs text-muted-foreground"
                    title={l.codigo}
                  >
                    <span className="block truncate">{l.codigo}</span>
                  </td>
                  <td className="min-w-0 px-3 py-3">
                    <Button
                      type="button"
                      variant="link"
                      onClick={(event) => {
                        event.stopPropagation();
                        onPreview(l.id);
                      }}
                      className="h-auto w-full min-w-0 justify-start p-0 text-left font-medium text-foreground no-underline transition hover:text-primary hover:no-underline"
                      title={l.nombre}
                    >
                      <span className="block min-w-0 truncate">{l.nombre}</span>
                    </Button>
                    <div
                      className="mt-0.5 truncate text-xs text-muted-foreground"
                      title={l.categoriaUnspsc?.[0] ?? "No informada"}
                    >
                      {l.categoriaUnspsc?.[0] ?? "—"}
                    </div>
                  </td>
                  <td className="min-w-0 px-3 py-3 text-muted-foreground" title={l.organismo}>
                    <span className="block truncate">{l.organismo}</span>
                  </td>
                  <td className="px-3 py-3">
                    {(() => {
                      const badge = canalBadge(l.tipoMP);
                      const contenido = (
                        <span
                          className={cn(
                            "block max-w-full truncate rounded-full px-2 py-0.5 text-center text-[10px] font-medium whitespace-nowrap",
                            badge.color,
                          )}
                        >
                          {badge.short}
                          {badge.canal && l.tipoMP ? ` · ${l.tipoMP}` : ""}
                        </span>
                      );
                      if (!badge.canal || !onCanalFilter) {
                        return (
                          <span className="block" title={badge.tooltip}>
                            {contenido}
                          </span>
                        );
                      }
                      return (
                        <button
                          type="button"
                          title={`${badge.tooltip} — filtrar por este canal`}
                          aria-label={`Filtrar por canal ${badge.label}`}
                          className="block w-full rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          onClick={(event) => {
                            event.stopPropagation();
                            onCanalFilter(badge.canal as CanalId);
                          }}
                        >
                          {contenido}
                        </button>
                      );
                    })()}
                  </td>

                  <td className="px-3 py-3">
                    {(() => {
                      const estadoMP = (l.estadoMP ?? "").toLowerCase();
                      const label = estadoMPLabels[estadoMP] ?? l.estadoMP ?? "—";
                      const elegibilidad = evaluarElegibilidad(l);
                      const sufijo = etiquetaPlazo(elegibilidad);
                      const color = !elegibilidad.postulable
                        ? "bg-muted text-muted-foreground"
                        : elegibilidad.estado === "por-vencer"
                          ? "bg-warning/15 text-warning"
                          : (estadoMPColors[estadoMP] ?? "bg-muted text-muted-foreground");
                      return (
                        <span
                          className={cn(
                            "block max-w-full truncate rounded-full px-2 py-0.5 text-center text-[10px] font-medium whitespace-nowrap",
                            color,
                          )}
                          title={`Estado en Mercado Público: ${label}. ${elegibilidad.motivo}`}
                        >
                          {sufijo ? `${label} · ${sufijo}` : label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-3">
                    {l.tracking?.estadoPipeline ? (
                      <span
                        className={cn(
                          "block max-w-full truncate rounded-full px-2 py-0.5 text-center text-[10px] font-medium whitespace-nowrap",
                          estadoColors[l.tracking.estadoPipeline as LicitacionEstado] ??
                            "bg-muted text-muted-foreground",
                        )}
                        title={`Pipeline interno: ${estadoLabels[l.tracking.estadoPipeline as LicitacionEstado] ?? l.tracking.estadoPipeline}`}
                      >
                        {estadoLabels[l.tracking.estadoPipeline as LicitacionEstado] ??
                          l.tracking.estadoPipeline}
                      </span>
                    ) : (
                      <span
                        className="block truncate text-[10px] text-muted-foreground"
                        title="Esta licitación no está en tu pipeline"
                      >
                        Sin seguimiento
                      </span>
                    )}
                  </td>

                  <td className="whitespace-nowrap px-3 py-3 text-right font-mono tabular-nums text-foreground">
                    {l.montoEstimado != null ? (
                      formatCLP(l.montoEstimado)
                    ) : (
                      <span className="text-xs text-muted-foreground">No público</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-center">
                    <div className="inline-flex items-center justify-center gap-1.5">
                      <div className="h-1 w-9 overflow-hidden rounded-full bg-muted">
                        <div
                          className={cn(
                            "h-full rounded-full",
                            score > 80
                              ? "bg-success"
                              : score > 65
                                ? "bg-warning"
                                : "bg-muted-foreground",
                          )}
                          style={{ width: `${score}%` }}
                        />
                      </div>
                      <span className="text-xs font-mono tabular-nums text-foreground">
                        {score}
                      </span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-center">
                    <span
                      className={cn(
                        "text-xs",
                        dias < 3
                          ? "text-destructive font-medium"
                          : dias < 7
                            ? "text-warning"
                            : "text-muted-foreground",
                      )}
                    >
                      {dias > 0 ? `${dias}d` : "—"}
                    </span>
                  </td>
                  <td className="min-w-0 px-3 py-3">
                    <div
                      className="flex min-w-0 items-center gap-1.5"
                      title={responsable !== "—" ? responsable : "Sin responsable"}
                    >
                      <div className="grid size-6 shrink-0 place-items-center rounded-full bg-primary/15 text-[10px] font-semibold text-primary">
                        {responsable !== "—"
                          ? responsable
                              .split(" ")
                              .map((n: string) => n[0])
                              .slice(0, 2)
                              .join("")
                          : "–"}
                      </div>
                      <span className="min-w-0 truncate text-xs text-muted-foreground">
                        {responsable !== "—" ? responsable : "Sin asignar"}
                      </span>
                    </div>
                  </td>
                  <td className="px-2 py-3 text-center">
                    <LicitacionActionsMenu
                      licitacion={l}
                      onPreview={() => onPreview(l.id)}
                      onTrackingChange={(tracking) => onTrackingChange(l.id, tracking)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-xs text-muted-foreground">
        <span className="whitespace-nowrap">
          Mostrando {start}–{end} de {total}
        </span>
        <div className="flex max-w-full items-center gap-1 overflow-x-auto pb-0.5">
          <button
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="px-2 py-1 rounded border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Anterior
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let p: number;
            if (totalPages <= 5) p = i + 1;
            else if (page <= 3) p = i + 1;
            else if (page >= totalPages - 2) p = totalPages - 4 + i;
            else p = page - 2 + i;
            return (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                className={cn(
                  "px-2 py-1 rounded border",
                  p === page
                    ? "border-border bg-muted text-foreground"
                    : "border-border hover:bg-muted text-muted-foreground",
                )}
              >
                {p}
              </button>
            );
          })}
          <button
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="px-2 py-1 rounded border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  );
}

/** Tablero por estado oficial: muestra todo el universo cargado, con o sin seguimiento. */
export function KanbanView({ data }: { data: LicitacionListItem[] }) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-4">
      {boardCols.map((col) => {
        const items = data.filter((l) => columnaDe(l) === col.id);
        return (
          <div
            key={col.id}
            className="w-72 shrink-0 rounded-xl border border-border bg-surface-2/40"
          >
            <div className="flex items-center justify-between p-3 border-b border-border">
              <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-medium", col.color)}>
                {col.label}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">{items.length}</span>
            </div>
            <div className="p-2 space-y-2 max-h-[600px] overflow-y-auto">
              {items.length === 0 ? (
                <p className="px-2 py-6 text-center text-[11px] text-muted-foreground">
                  Sin licitaciones en este estado con los filtros actuales.
                </p>
              ) : (
                items.map((l) => {
                  const badge = canalBadge(l.tipoMP);
                  const tracking = l.tracking;
                  const cierre = l.fechaCierre
                    ? new Date(l.fechaCierre).toLocaleDateString("es-CL", {
                        timeZone: "America/Santiago",
                        day: "2-digit",
                        month: "2-digit",
                      })
                    : "Sin fecha";
                  return (
                    <Link
                      key={l.id}
                      to="/licitaciones/$id"
                      params={{ id: l.id }}
                      className="block rounded-lg border border-border bg-card p-3 hover:border-border-strong hover:shadow-elegant transition"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <span className="font-mono text-[10px] text-muted-foreground">
                          {l.codigo}
                        </span>
                        <span
                          className={cn("text-[10px] px-1.5 py-0.5 rounded", badge.color)}
                          title={badge.tooltip}
                        >
                          {badge.short}
                        </span>
                      </div>
                      <div className="text-xs font-medium text-foreground line-clamp-2 mb-1">
                        {l.nombre}
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate mb-2">
                        {l.organismo || "Por enriquecer"}
                      </div>
                      <div className="flex items-center justify-between text-[10px] font-mono text-muted-foreground">
                        <span>
                          {l.montoEstimado != null ? formatCLP(l.montoEstimado) : "No público"}
                        </span>
                        <span>Cierre {cierre}</span>
                      </div>
                      {tracking && (
                        <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-[10px]">
                          <span
                            className={cn(
                              "px-1.5 py-0.5 rounded",
                              estadoColors[tracking.estadoPipeline],
                            )}
                          >
                            {estadoLabels[tracking.estadoPipeline]}
                          </span>
                          <span className="text-muted-foreground truncate max-w-[8rem]">
                            {tracking.responsable ?? "Sin asignar"}
                          </span>
                        </div>
                      )}
                    </Link>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

/** Partes de fecha en horario de Chile. */
function partesChile(value: string | Date): { y: number; m: number; d: number } {
  const fecha = typeof value === "string" ? new Date(value) : value;
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Santiago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const [y, m, d] = fmt.format(fecha).split("-").map(Number);
  return { y, m: m - 1, d };
}

/** Calendario mensual real de cierres, con navegación entre meses. */
export function CalendarView({ data }: { data: LicitacionListItem[] }) {
  const hoy = partesChile(new Date());
  const [cursor, setCursor] = useState<{ y: number; m: number }>({ y: hoy.y, m: hoy.m });

  const primerDia = new Date(cursor.y, cursor.m, 1);
  const diasEnMes = new Date(cursor.y, cursor.m + 1, 0).getDate();
  // Lunes como primer día de la semana.
  const offset = (primerDia.getDay() + 6) % 7;
  const celdas = Math.ceil((offset + diasEnMes) / 7) * 7;

  const eventosPorDia = new Map<number, LicitacionListItem[]>();
  for (const l of data) {
    if (!l.fechaCierre) continue;
    const p = partesChile(l.fechaCierre);
    if (p.y !== cursor.y || p.m !== cursor.m) continue;
    const lista = eventosPorDia.get(p.d) ?? [];
    lista.push(l);
    eventosPorDia.set(p.d, lista);
  }

  const mover = (delta: number) => {
    const fecha = new Date(cursor.y, cursor.m + delta, 1);
    setCursor({ y: fecha.getFullYear(), m: fecha.getMonth() });
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-elegant">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold text-foreground">
          {MESES[cursor.m]} {cursor.y}
        </h3>
        <div className="flex gap-1">
          <button
            aria-label="Mes anterior"
            onClick={() => mover(-1)}
            className="px-2 py-1 text-xs rounded border border-border hover:bg-muted"
          >
            ‹
          </button>
          <button
            onClick={() => setCursor({ y: hoy.y, m: hoy.m })}
            className="px-2 py-1 text-xs rounded border border-border hover:bg-muted"
          >
            Hoy
          </button>
          <button
            aria-label="Mes siguiente"
            onClick={() => mover(1)}
            className="px-2 py-1 text-xs rounded border border-border hover:bg-muted"
          >
            ›
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
        {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => (
          <div
            key={d}
            className="bg-surface-2/40 text-[10px] uppercase tracking-wider text-muted-foreground font-medium text-center py-2"
          >
            {d}
          </div>
        ))}
        {Array.from({ length: celdas }).map((_, i) => {
          const dia = i - offset + 1;
          const dentroDelMes = dia >= 1 && dia <= diasEnMes;
          const eventos = dentroDelMes ? (eventosPorDia.get(dia) ?? []) : [];
          const esHoy = dentroDelMes && cursor.y === hoy.y && cursor.m === hoy.m && dia === hoy.d;
          return (
            <div
              key={i}
              className={cn(
                "min-h-[88px] p-1.5 transition",
                dentroDelMes ? "bg-card hover:bg-muted/40" : "bg-surface-2/20",
                esHoy && "ring-1 ring-inset ring-primary",
              )}
            >
              {dentroDelMes && (
                <>
                  <div
                    className={cn(
                      "text-[11px] mb-1",
                      esHoy ? "text-primary font-semibold" : "text-muted-foreground",
                    )}
                  >
                    {dia}
                  </div>
                  {eventos.slice(0, 3).map((e) => (
                    <Link
                      key={e.id}
                      to="/licitaciones/$id"
                      params={{ id: e.id }}
                      title={e.nombre}
                      className="block text-[10px] bg-primary/10 text-primary rounded px-1.5 py-0.5 truncate mb-0.5 hover:bg-primary/20"
                    >
                      {e.codigo}
                    </Link>
                  ))}
                  {eventos.length > 3 && (
                    <div className="text-[10px] text-muted-foreground px-1.5">
                      +{eventos.length - 3} más
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
